import React, { useEffect, useState } from 'react';
import { MenuItem, TextField, TextFieldProps, Grid, GridProps } from '@material-ui/core';

import ExtensionField, { ExtFieldProps } from './ExtensionField';
import PhoneField, { PhoneFieldProps } from '../PhoneField';

export type PhoneFieldGroupProps = {
  ContainerComponentProps?: GridProps;
  PhoneTypeSelectProps?: TextFieldProps;
  PhoneFieldProps?: PhoneFieldProps;
  ExtFieldProps?: ExtFieldProps;
  value: PhoneFields;
  onChange: (value: PhoneFields) => void;
  onBlur?: (value: PhoneFields) => void;
  error?: string | undefined;
  typeError?: string | undefined;
};

export type PhoneFields = {
  [key: string]: string | boolean | undefined;
  type?: 'Home' | 'Mobile' | 'Business' | 'Office' | 'Fax' | '' | undefined;
  phoneNumber?: string | undefined;
  ext?: string | undefined;
  isPrimary?: boolean;
  id?: string; // Note: ID is only used for frontend development
};

export const Home = 'Home';
export const Mobile = 'Mobile';
export const Business = 'Business';
export const Office = 'Office';
export const Fax = 'Fax';
export const ExtensionPhoneTypes = [Office, Fax, Business];

export const PhoneFieldGroup = ({
  ContainerComponentProps,
  PhoneTypeSelectProps,
  PhoneFieldProps,
  ExtFieldProps,
  value: valueProp,
  error,
  typeError,
  onChange = () => undefined,
  onBlur = () => undefined,
}: PhoneFieldGroupProps): JSX.Element => {
  const typeName = PhoneTypeSelectProps?.name || 'type';
  const phoneNumberName = PhoneFieldProps?.name || 'phoneNumber';
  const extName = ExtFieldProps?.name || 'ext';

  const [state, setState] = useState<PhoneFields>(
    valueProp || {
      [typeName]: '',
      [phoneNumberName]: '',
      [extName]: '',
      isPrimary: false,
    },
  );

  useEffect(() => {
    if (valueProp) {
      setState(valueProp);
    }
  }, [valueProp]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newState = { ...state, [name]: value };

    if (state[typeName] == Home || state[typeName] == Mobile) {
      newState['ext'] = '';
    }

    updateState(newState);
  };

  const handleBlur = () => {
    if (onBlur) onBlur(state);
  };

  const updateState = (state: PhoneFields) => {
    setState(state);
    onChange(state);
  };

  return (
    <Grid container {...ContainerComponentProps}>
      <Grid item xs={12} sm={3} md={4}>
        <TextField
          data-testid="phone-type"
          inputProps={{ 'data-testid': 'phone-type-input' }}
          {...PhoneTypeSelectProps}
          fullWidth
          select
          className={PhoneTypeSelectProps?.className}
          value={state[typeName]}
          name={typeName}
          error={!!typeError}
          helperText={typeError}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          <MenuItem value={Home} data-testid="home">
            {Home}
          </MenuItem>
          <MenuItem value={Mobile} data-testid="mobile">
            {Mobile}
          </MenuItem>
          <MenuItem value={Business} data-testid="business">
            {Business}
          </MenuItem>
          {/**
              TODO: validate this is a valid type
              Doesn't appear in the req
              https://iwanttss.atlassian.net/wiki/spaces/NG/pages/1300562277/Phone+Number+Control
              <MenuItem value={Office}>{Office}</MenuItem>
            */}
          <MenuItem value={Fax} data-testid="fax">
            {Fax}
          </MenuItem>
        </TextField>
      </Grid>
      <Grid item xs>
        <PhoneField
          data-testid="phone-field-input"
          inputProps={{ 'data-testid': 'phone-field-number-input' }}
          FormHelperTextProps={
            {
              'data-testid': 'phone-field-error-text',
            } as { [key: string]: string }
          }
          {...PhoneFieldProps}
          fullWidth
          onChange={handleChange}
          onBlur={handleBlur}
          name={phoneNumberName}
          value={state[phoneNumberName]}
          error={!!error}
          helperText={error}
        />
      </Grid>
      {ExtensionPhoneTypes.includes(state[typeName] as string) && (
        <Grid item xs={4} sm={3}>
          <ExtensionField
            {...ExtFieldProps}
            name={extName}
            value={state[extName]}
            onChange={handleChange}
            data-testid="phone-ext-field-input"
          />
        </Grid>
      )}
    </Grid>
  );
};

export default PhoneFieldGroup;
